import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';

import { useAuth } from '../contexts/AuthContext';
import { usePets } from '../contexts/PetContext';
import { db } from '../config/firebase';
import { logger } from '../services/logger';

const log = logger.createScope('PrivateRoute');

interface PrivateRouteProps {
  children: React.ReactNode;
}

interface UserDocShape {
  onboardingComplete?: boolean;
  access?: 'fnf' | string | null;
  subscription?: { status?: string };
}

/**
 * Auth + onboarding-completion gate.
 *
 * Auth: requires a signed-in user. Email verification is NOT enforced here
 * (soft banner from the Layout handles that without blocking access).
 *
 * Onboarding completion (Phase 4): when the user is signed in but has not
 * completed onboarding, route them to the right entry point:
 *   - access === 'fnf' OR subscription is trialing/active → /onboarding
 *   - otherwise → /trial (they need to subscribe or redeem a code first)
 *
 * Semantics for `users/{uid}.onboardingComplete`:
 *   - `undefined` or `true` → render through.
 *   - `false` → bounce, UNLESS the user already has at least one pet (which
 *     means they completed onboarding pre-stamp). In that case we self-heal
 *     by writing `true` and rendering through. This protects existing prod
 *     users from being forced through onboarding again — same effect as the
 *     Phase 6 migration, applied lazily on first authenticated route hit.
 *
 * /onboarding and /trial are exempt from the redirect to prevent loops while
 * the user is actively completing those flows.
 */
const EXEMPT_PATHS = ['/onboarding', '/trial'];

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const { currentUser, loading } = useAuth();
  const { pets, loading: petsLoading } = usePets();
  const location = useLocation();

  // userDoc tri-state:
  //   undefined → snapshot still pending
  //   null      → snapshot landed but doc missing (rare; e.g., race vs. AuthContext.ensureUserDocument)
  //   object    → loaded
  const [userDoc, setUserDoc] = useState<UserDocShape | null | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!currentUser) {
      setUserDoc(undefined);
      return;
    }
    setUserDoc(undefined);
    const unsub = onSnapshot(
      doc(db, 'users', currentUser.uid),
      (snap) => {
        setUserDoc((snap.data() as UserDocShape) ?? null);
      },
      (err) => {
        log.warn('Failed to subscribe to user doc — rendering through', {
          err: err as Error,
        });
        // Fail-open: don't lock the user out of their own app on a Firestore hiccup.
        setUserDoc(null);
      },
    );
    return unsub;
  }, [currentUser]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">
          Checking authentication...
        </Typography>
      </Box>
    );
  }

  if (!currentUser) {
    log.debug('No authenticated user, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  // Wait for the first snapshot before deciding whether to redirect — avoids
  // a brief flash of children mounting only to be unmounted by a redirect.
  if (userDoc === undefined) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const path = location.pathname;
  if (EXEMPT_PATHS.includes(path)) {
    return <>{children}</>;
  }

  const onboardingComplete = userDoc?.onboardingComplete;
  if (onboardingComplete === false) {
    // Self-heal: if the user already has pets, they finished onboarding
    // before we started stamping `onboardingComplete`. Treat as complete and
    // backfill the flag so we don't keep doing this read every navigation.
    // Wait for petsLoading to settle so we don't briefly bounce a fresh
    // login before the pets list arrives.
    if (petsLoading) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (pets.length > 0) {
      // Fire-and-forget — failure is non-fatal; we'll re-attempt next render.
      void setDoc(
        doc(db, 'users', currentUser.uid),
        { onboardingComplete: true },
        { merge: true },
      ).catch((err) => {
        log.warn('Self-heal onboardingComplete write failed', {
          err: err as Error,
        });
      });
      return <>{children}</>;
    }
    const access = userDoc?.access;
    const status = userDoc?.subscription?.status;
    const subActive = status === 'trialing' || status === 'active';
    const target = access === 'fnf' || subActive ? '/onboarding' : '/trial';
    log.debug('Onboarding incomplete — redirecting', {
      path,
      target,
      access,
      status,
    });
    return <Navigate to={target} replace />;
  }

  // onboardingComplete === true OR === undefined (legacy) → render through.
  return <>{children}</>;
};

export default PrivateRoute;
